import { UseQueryOptions, useMutation, useQuery } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { selectAuthToken } from 'src/store/selectors';
import {
  getDataSourceById,
  getDataSourceResearchers,
  getOrganizationsByDataSourceId,
  getOrganizationUnitsByCohortId,
  getOrganizationUnitsByDataSourceId,
  updateDataSource,
  UpdateDataSourceDTO,
} from './data-sources';
import { configuration } from 'src/configuration';
import { showToast } from 'src/store/toast';
import { useGetUserOverview } from './users-queries';
import { DataSourceResearcherQueryDTO } from 'src/types/data-sources';
import { OrganizationsResponse, OrganizationUnitsResponse } from './data-sources-dto';

export function useGetDataSourceById(dataSourceId: string | undefined) {
  const token = useSelector(selectAuthToken);

  return useQuery({
    queryKey: ['data-source', dataSourceId ? dataSourceId : ''],
    queryFn: async () => {
      if (dataSourceId) {
        return await getDataSourceById({ token, dataSourceId });
      }
    },
    staleTime: configuration.queriesStaleTime,
  });
}

export function useGetDataSourceResearchers(dataSourceId: string | undefined) {
  const token = useSelector(selectAuthToken);

  return useQuery<DataSourceResearcherQueryDTO | null>({
    queryKey: ['data-source-researchers', dataSourceId],
    queryFn: async () => {
      if (dataSourceId) {
        return await getDataSourceResearchers({ token, dataSourceId });
      }
      return null;
    },
    staleTime: configuration.queriesStaleTime,
  });
}

export function useUpdateDataSourceMutation() {
  const token = useSelector(selectAuthToken);
  const dispatch = useDispatch();
  const getUserOverview = useGetUserOverview();

  return useMutation({
    mutationFn: (updatedDataSource: UpdateDataSourceDTO) => {
      return updateDataSource({ token, dataSourceToUpdate: updatedDataSource });
    },
    onSuccess: () => {
      dispatch(
        showToast({
          type: 'success',
          message: 'Data source updated successfully',
        }),
      );
      getUserOverview.refetch();
    },
  });
}

export function useGetOrganizationUnitsByDataSourceId<TData = OrganizationUnitsResponse>(
  { dataSourceId }: { dataSourceId?: string },
  options?: Omit<UseQueryOptions<OrganizationUnitsResponse, Error, TData>, 'queryKey' | 'queryFn'>,
) {
  const token = useSelector(selectAuthToken);

  return useQuery<OrganizationUnitsResponse, Error, TData>({
    queryKey: ['organization-unit', dataSourceId],
    queryFn: async () => {
      if (!dataSourceId) {
        throw new Error('dataSourceId is required');
      }
      return await getOrganizationUnitsByDataSourceId({ token, dataSourceId });
    },
    enabled: !!dataSourceId,
    ...options,
  });
}

export function useGetOrganizationsByDataSourceId<TData = OrganizationsResponse>(
  { dataSourceId }: { dataSourceId?: string },
  options?: Omit<UseQueryOptions<OrganizationsResponse, Error, TData>, 'queryKey' | 'queryFn'>,
) {
  const token = useSelector(selectAuthToken);

  return useQuery<OrganizationsResponse, Error, TData>({
    queryKey: ['organizations', dataSourceId],
    queryFn: async () => {
      if (!dataSourceId) {
        throw new Error('dataSourceId is required');
      }
      return await getOrganizationsByDataSourceId({ token, dataSourceId });
    },
    enabled: !!dataSourceId,
    ...options,
  });
}

export function useGetCohortOrganizationUnits({ dataSourceId }: { dataSourceId: string }) {
  const token = useSelector(selectAuthToken);

  return useQuery({
    queryKey: ['cohort-organization-units', dataSourceId],
    queryFn: async () => {
      if (dataSourceId) {
        return await getOrganizationUnitsByDataSourceId({ token, dataSourceId });
      }
    },
  });
}

export function useGetOrganizationUnitsByCohortId({
  dataSourceId,
  cohortId,
}: {
  dataSourceId: string;
  cohortId: string;
}) {
  const token = useSelector(selectAuthToken);

  return useQuery({
    queryKey: ['cohort-organization-units', dataSourceId, cohortId],
    queryFn: async () => {
      if (dataSourceId) {
        return await getOrganizationUnitsByCohortId({ token, dataSourceId, cohortId });
      }
    },
  });
}
